/**
 * Canonical constants for the WTC customer-folders Drive area.
 *
 * The customer root holds ONE folder per company (company-level — all contacts
 * at a company share it). Historically this id was copy-pasted into 5+ files
 * (`customer-folder-helpers.ts`, `CompanyDrive.tsx`, `ClientDriveTab.tsx`,
 * `SaveAttachmentsToGoogleDriveModal.types.ts`, quotes `constants.ts`). Those
 * re-export from here so there is a single source of truth.
 *
 * The root lives INSIDE a Shared Drive, so all create/copy/update calls against
 * it must pass `supportsAllDrives: true`.
 */

/** Root Drive folder that holds every company's folder (one subfolder per company). */
export const CUSTOMER_DRIVE_ROOT_FOLDER_ID =
  process.env.CUSTOMER_DRIVE_ROOT_FOLDER_ID?.trim() || "196en7iu_hwboClixVXQiqX1YUzzhD-Kz";

/**
 * Template folder cloned into each new company folder so it starts pre-configured
 * with the standard subfolder structure. The impersonated service-account subject
 * (`COMPANY_DRIVE_SUBJECT`) must have at least viewer access to it.
 *
 * ## The previous id was DEAD, and nothing said so
 *
 * The old default `1PqYtNodE5shA6Y0N6wq2JQ4Bm3dX3PZ_` answered **404 File not
 * found** for the delegated subject (verified against prod Drive 2026-09-17).
 * The template folder had been deleted at some point, so `cloneDriveFolderTree`
 * was cloning nothing and every company folder provisioned since arrived EMPTY —
 * measured: only 2 of 25 sampled company folders had any children at all.
 *
 * It was invisible because the failure heals itself into silence:
 * `getCompanySubfolderId` find-or-creates each subfolder on first use, so the
 * structure appears piecemeal, in whatever order things happen to be filed,
 * and looks like a folder nobody has used yet rather than a broken clone.
 * (`mcp__hub__browse_drive` made it worse by reporting the dead id as
 * `hasAccess: true, 0 items` — an empty listing, not a 404.)
 *
 * The replacement was created by the delegated subject itself, so the
 * viewer-access requirement above holds by construction rather than by a grant
 * somebody has to remember. Its eight subfolders are what the evidence supports:
 * `01_WTC-Deliverables` / `02_SM-Deliverables` were in real folders cloned from
 * the old template (they are deliberately NOT in `COMPANY_SUBFOLDER` — nothing
 * files into them automatically), `03`–`07` are `COMPANY_SUBFOLDER`, and `08` is
 * the mail bucket.
 *
 * **If this 404s again, that is the bug** — check the id resolves with
 * `files.get` before assuming a company folder is merely unused.
 */
export const COMPANY_FOLDER_TEMPLATE_ID =
  process.env.COMPANY_DRIVE_TEMPLATE_FOLDER_ID?.trim() || "1t0n6mqzcwEKejicYV7e7-ypbReYIWZiv";

/**
 * Workspace user the hub service account impersonates (domain-wide delegation)
 * for company-folder provisioning/upload. MUST be a content manager on the
 * shared drive containing the customer root AND have viewer access to the
 * template folder. Overridable per environment.
 */
export const COMPANY_DRIVE_SUBJECT =
  process.env.COMPANY_DRIVE_SUBJECT?.trim() ||
  process.env.GDRIVE_QUOTE_LOOKUP_SUBJECT?.trim() ||
  "miclainkeffeler@westtn.consulting";

/** Browser URL for a Drive folder id. */
export function companyDriveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}
